<template>
  <div>
    <h1 class="pop-auth__h1">{{ title }}</h1>
    <p class="pop-auth__sub"><slot name="intro"></slot></p>

    <code-input ref="code" :disabled="busy || locked" :invalid="state === 'bad'" @complete="submit" @change="state = 'idle'" />

    <div v-if="state === 'bad'" class="pop-auth__err" data-test="code-error">
      That code is not right. {{ attemptsLeft }} {{ attemptsLeft === 1 ? 'attempt' : 'attempts' }} left before we lock this for 15 minutes.
    </div>
    <div v-else-if="state === 'expired'" class="pop-auth__err">That code has expired. Resend to get a new one.</div>
    <div v-else-if="state === 'ok'" class="pop-auth__err code-step__ok">Verified. Taking you in...</div>
    <div v-else-if="state === 'error'" class="pop-auth__err">{{ errorMessage }}</div>
    <div v-if="locked" class="pop-auth__box pop-auth__box--danger">
      Too many attempts. Try again in {{ lockClock }}.
    </div>

    <slot name="extra"></slot>

    <div class="code-step__resend">
      <span v-if="resendIn > 0">Resend code in 0:{{ String(resendIn).padStart(2, '0') }}</span>
      <button v-else type="button" class="pop-auth__link" :disabled="busy || locked" data-test="code-resend" @click="resend">Resend code</button>
    </div>

    <div class="pop-auth__box pop-auth__box--muted">
      Not seeing it? Check spam, or make sure <b>noreply@pageoptimizer.pro</b> is allowed. The code expires in 10 minutes.
      <a class="pop-auth__link" href="mailto:support@pageoptimizer.pro">Contact support</a>
    </div>
  </div>
</template>

<script>
import CodeInput from './CodeInput.vue'

// Shared body of the signup verification and new-device login screens: the 6 boxes, the
// attempts / lock / expired / verified states and the 45-second resend countdown. The page
// provides `verify(code)` and `resend()` returning the backend payload ({ action, ... }).
export default {
  name: 'CodeStep',
  components: { CodeInput },
  props: {
    title: { type: String, required: true },
    verify: { type: Function, required: true },
    resendCode: { type: Function, required: true },
    resendAvailableAt: { type: Number, default: 0 }, // epoch ms, survives a refresh via the store
    lockedUntil: { type: Number, default: 0 },
  },
  data() {
    return {
      busy: false,
      state: 'idle', // idle | bad | expired | ok | error
      attemptsLeft: 5,
      errorMessage: '',
      now: Date.now(),
      ticker: null,
      localResendAt: this.resendAvailableAt,
      localLockedUntil: this.lockedUntil,
    }
  },
  computed: {
    resendIn() {
      return Math.max(0, Math.ceil((this.localResendAt - this.now) / 1000))
    },
    locked() {
      return this.localLockedUntil > this.now
    },
    lockClock() {
      const total = Math.max(0, Math.ceil((this.localLockedUntil - this.now) / 1000))
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
    },
  },
  watch: {
    resendAvailableAt(value) {
      this.localResendAt = value
    },
    lockedUntil(value) {
      this.localLockedUntil = value
    },
  },
  mounted() {
    this.ticker = setInterval(() => { this.now = Date.now() }, 500)
  },
  beforeDestroy() {
    clearInterval(this.ticker)
  },
  methods: {
    async submit(code) {
      if (this.busy || this.locked) return
      this.busy = true
      this.errorMessage = ''
      try {
        const data = (await this.verify(code)) || {}
        this.apply(data)
      } catch (e) {
        console.log(e)
        this.fail('Something went wrong. Please try again.')
      } finally {
        this.busy = false
      }
    },
    async resend() {
      if (this.busy || this.locked) return
      this.busy = true
      try {
        const data = (await this.resendCode()) || {}
        if (data.status === 'SUCCESS') {
          this.setResend(data.resendAfterSeconds || 45)
          this.state = 'idle'
          this.$refs.code && this.$refs.code.reset()
          this.$notify({ group: 'info', type: 'success', text: 'We sent a new code.' })
        } else if (data.action === 'RESEND_COOLDOWN') {
          this.setResend(data.retryAfterSeconds || 45)
        } else if (data.action === 'CODE_LOCKED') {
          this.lock(data.retryAfterSeconds)
        } else if (data.action === 'PENDING_INVALID') {
          this.$emit('expired')
        } else {
          this.fail(data.msg || 'Could not send a new code. Please try again.')
        }
      } catch (e) {
        console.log(e)
        this.fail('Could not send a new code. Please try again.')
      } finally {
        this.busy = false
      }
    },
    apply(data) {
      switch (data.action) {
        case 'INVALID_CODE':
          this.attemptsLeft = data.attemptsLeft != null ? data.attemptsLeft : this.attemptsLeft - 1
          this.state = 'bad'
          this.$refs.code && this.$refs.code.reset()
          break
        case 'CODE_LOCKED':
          this.lock(data.retryAfterSeconds)
          break
        case 'CODE_EXPIRED':
          this.state = 'expired'
          this.$refs.code && this.$refs.code.reset()
          break
        case 'PENDING_INVALID':
          this.$emit('expired')
          break
        default:
          if (data.status === 'SUCCESS') {
            this.state = 'ok'
            setTimeout(() => this.$emit('verified', data), 500)
          } else {
            this.fail(data.msg || 'Something went wrong. Please try again.')
          }
      }
    },
    fail(message) {
      this.state = 'error'
      this.errorMessage = message
      this.$refs.code && this.$refs.code.reset()
    },
    setResend(seconds) {
      this.localResendAt = Date.now() + Number(seconds) * 1000
      this.$emit('resend-at', this.localResendAt)
    },
    lock(seconds) {
      this.state = 'idle'
      this.localLockedUntil = Date.now() + Number(seconds || 900) * 1000
      this.$emit('locked-until', this.localLockedUntil)
    },
  },
}
</script>

<style scoped lang="scss">
@import './auth-tokens.scss';

.code-step__ok { color: $pop-success; }
.code-step__resend { margin-top: 22px; font-size: 13px; color: $pop-ink-muted; text-align: center; }
</style>
